<template>
  <div 
    class="z-[1] group relative flex flex-col overflow-hidden rounded-sm hover:duration-300 hover:ease-in-out"
    :class="product.status === 'sold-out' ? 'bg-gray-100' : 'bg-white'"
  >
    <!-- Closing Badge -->
    <div v-if="product.status !== 'sold-out'" class="absolute top-2 right-2 rounded-sm bg-white text-red-500 px-2 py-1 shadow-xl z-20">
      {{ t('pages.collection.productCard.closingSoon') }}
    </div>
    
    <!-- Red Dot for Sold Out Items -->
    <div v-if="product.status === 'sold-out'" class="absolute z-10 flex items-center justify-center w-full h-1/2">
      <div class="w-24 h-24 rounded-full bg-red-600 shadow-lg text-center flex items-center justify-center shadow-xl">
        <span class="text-white uppercase font-thin text-sm tracking-widest">{{ t('pages.collection.productCard.soldBadge') }}</span>
      </div>
    </div>
    
    <button
      @click="openPreview"
      class="p-4 mb-8 aspect-[3/4] w-full bg-gray-100 group-hover:zoom-in-110 group-hover:p-0 group-hover:ease-in-out group-hover:scale-110 transition-all duration-500 ease-in-out sm:h-96 cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
      :class="product.status === 'sold-out' ? 'group-hover:opacity-75' : ''"
      :aria-label="t('pages.collection.productCard.previewImage', { name: localizedName })"
    >
      <template v-if="resolvedImage.provider">
        <NuxtImg
          v-bind="resolvedImage"
          :provider="resolvedImage.provider"
          :alt="localizedDescription"
          class="rounded-sm aspect-[3/4] w-full object-cover sm:aspect-auto sm:h-96"
          sizes="sm:240px md:320px lg:400px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </template>
      <img
        v-else
        :src="resolvedImage.src"
        :alt="localizedDescription"
        class="rounded-sm aspect-[3/4] w-full object-cover sm:aspect-auto sm:h-96"
      />
    </button>
    <div class="flex flex-1 flex-col space-y-2 p-4 relative z-10">
      <h5 class="text-xs font-light text-gray-700 relative top-auto left-auto absolute -top-2 left-4 uppercase tracking-widest">{{ localizedMaterial }}</h5>
      <div class="flex items-center justify-between">
        <NuxtLink :to="`/of/${createArtistSlug(product.brand)}`" class="relative z-20 text-xl font-medium text-gray-500 font-serif hover:text-gray-700 transition-colors">
          {{ product.brand }}
        </NuxtLink>
      </div>
      <h3 class="text-lg font-medium text-gray-900">
        <NuxtLink :to="localePath('sample-product-page')" class="relative z-20">
          {{ localizedName }}
        </NuxtLink>
      </h3>
      <p v-if="localizedDescription" class="text-sm text-gray-500">{{ localizedDescription }}</p>
      <div v-if="skuCode" class="text-xs text-gray-400 font-mono mt-1">
        SKU: {{ skuCode }}
      </div>
      <div class="flex flex-1 flex-col justify-end">
        <div class="flex items-baseline gap-2">
          <p v-if="originalPrice && originalPrice !== displayPrice" class="text-sm text-gray-400 line-through">
            € {{ formatPrice(originalPrice) }}
          </p>
          <p class="text-xl text-gray-900">€ {{ formatPrice(displayPrice) }}</p>
        </div>
      </div>
    </div>

    <!-- Image Preview Modal -->
    <ProductImagePreview
      :is-open="isPreviewOpen"
      :product="product"
      @close="closePreview"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from '#imports'
import { createArtistSlug, useArtists } from '~/composables/useArtists'
import { useImageCdn } from '~/composables/useImageCdn'
import type { SKU } from '~/types/sku'
import ProductImagePreview from '~/components/ProductImagePreview.vue'

interface CollectionProduct {
  id: number | string
  brand: string
  name: string
  description: string
  material: string
  price: string | number
  images?: string[]
  image?: string // Legacy support
  status?: string
  sku?: SKU // Original SKU data if available
}

const props = defineProps<{
  product: CollectionProduct
}>()

const { t, locale } = useI18n()
const { resolveForDisplay } = useImageCdn()
const localePath = useLocalePath()
const { dataSource, dataReason, artistsData } = useArtists()

const localizedName = computed(() => props.product.name)

// Check if we're in demo mode (mock data)
// Read directly from the API response meta to ensure consistency between SSR and client
// This avoids hydration mismatches by using the same source of truth
const isDemoMode = computed(() => {
  // Always read from artistsData meta first (the authoritative source)
  // This ensures we get the same value during SSR and client hydration
  // The watch that sets dataSource/dataReason is just for convenience, but we
  // should always trust the meta object directly from the API response
  const meta = artistsData.value?.meta
  
  // If meta is available, use it directly (most reliable)
  if (meta) {
    return meta.source === 'fallback' && meta.reason === 'use_mock_data'
  }
  
  // Fallback to refs only if meta is not available (shouldn't happen in normal flow)
  // This provides a safety net but shouldn't be the primary path
  const source = dataSource.value
  const reason = dataReason.value
  return source === 'fallback' && reason === 'use_mock_data'
})

const localizedDescription = computed(() => {
  // If we have SKU data with locale-specific descriptions, use those
  if (props.product.sku) {
    if (locale.value === 'de' && props.product.sku.descriptionDe) {
      return props.product.sku.descriptionDe
    } else if (locale.value === 'it' && props.product.sku.descriptionIt) {
      return props.product.sku.descriptionIt
    } else if (props.product.sku.descriptionEn) {
      return props.product.sku.descriptionEn
    }
  }
  return props.product.description
})

const localizedMaterial = computed(() => props.product.material)

// Get first image from images array (or fallback to image property for backward compatibility)
const productImage = computed(() => {
  if (Array.isArray(props.product.images) && props.product.images.length > 0) {
    return props.product.images[0]
  }
  // Legacy support
  return props.product.image || ''
})

// Resolve image through CDN
const resolvedImage = computed(() => {
  const imagePath = productImage.value
  if (!imagePath) {
    if (import.meta.client && process.env.NODE_ENV === 'development') {
      console.warn('[CollectionProductCard] No image for product:', props.product.name, props.product.sku)
    }
    return { src: '', provider: undefined }
  }
  
  const resolved = resolveForDisplay(imagePath, props.product.sku?.slugEn)
  
  if (import.meta.client && process.env.NODE_ENV === 'development') {
    if (!resolved.src || resolved.src === imagePath) {
      console.log('[CollectionProductCard] Image resolution for', props.product.name, ':', {
        original: imagePath,
        resolved: resolved.src,
        provider: resolved.provider
      })
    }
  }
  
  return resolved
})

// SKU code
const skuCode = computed(() => props.product.sku?.sku)

// Price handling - support discounted prices
const displayPrice = computed(() => {
  // If SKU has discounted price, use that
  if (props.product.sku?.discountedPrice) {
    return props.product.sku.discountedPrice
  }
  return props.product.price
})

const originalPrice = computed(() => {
  // If we're showing discounted price, show original price as well
  if (props.product.sku?.discountedPrice && props.product.sku.price) {
    return props.product.sku.price
  }
  return undefined
})

const formatPrice = (price: string | number | undefined): string => {
  if (!price) return '0.00'
  const numPrice = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.,]/g, '').replace(',', '.')) : price
  if (isNaN(numPrice)) return String(price)
  return numPrice.toFixed(2)
}

// Image preview modal state
const isPreviewOpen = ref(false)

const getProductHash = (productId: number | string) => {
  return `#product-${productId}`
}

const openPreview = () => {
  isPreviewOpen.value = true
  // Update URL hash when opening preview
  if (import.meta.client) {
    const hash = getProductHash(props.product.id)
    window.history.replaceState(null, '', `${window.location.pathname}${hash}`)
  }
}

const closePreview = () => {
  isPreviewOpen.value = false
  // Remove hash from URL when closing
  if (import.meta.client && window.location.hash) {
    const newUrl = window.location.href.split('#')[0]
    window.history.replaceState(null, '', newUrl)
  }
}

// Check URL hash on mount and when route changes
let hashChangeHandler: (() => void) | null = null

onMounted(() => {
  if (import.meta.client) {
    const checkHash = () => {
      const hash = window.location.hash
      const expectedHash = getProductHash(props.product.id)
      
      if (hash === expectedHash) {
        // Hash matches this product - open preview
        isPreviewOpen.value = true
      } else if (isPreviewOpen.value && hash !== expectedHash) {
        // Hash changed to something else or was removed - close preview
        isPreviewOpen.value = false
      }
    }
    
    // Check hash on initial mount
    checkHash()
    
    // Listen for hash changes (e.g., browser back/forward)
    hashChangeHandler = checkHash
    window.addEventListener('hashchange', hashChangeHandler)
  }
})

onUnmounted(() => {
  if (import.meta.client && hashChangeHandler) {
    window.removeEventListener('hashchange', hashChangeHandler)
  }
})
</script>


